<template>
  <div class="min-h-screen bg-white">
    <Header />

    <main>

      <!-- ═══ HERO ═══ -->
      <section class="relative flex min-h-[80vh] items-center overflow-hidden bg-white pt-24 pb-16 md:pt-32 md:pb-20">
        <div class="paw-pattern pointer-events-none absolute inset-0 opacity-[0.35]" aria-hidden="true" />

        <div class="relative mx-auto w-full max-w-6xl px-4 md:px-6">
          <div class="mx-auto max-w-3xl text-center">
            <h1 class="text-5xl font-bold leading-[1.05] tracking-tight text-foreground md:text-6xl lg:text-7xl">
              {{ $ts('hero.title') }}
              <span class="block text-vet">{{ $ts('hero.titleHighlight') }}</span>
            </h1>
            <p class="mx-auto mt-6 max-w-xl text-lg text-muted-foreground md:text-xl">
              {{ $ts('hero.subtitle') }}
            </p>

            <!-- CTAs -->
            <div class="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <a
                :href="consoleUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex h-14 items-center gap-2 rounded-full bg-foreground px-8 text-base font-semibold text-background transition-opacity hover:opacity-80"
              >
                {{ $ts('hero.primaryCta') }}
                <Icon name="tabler:arrow-right" size="18" />
              </a>
              <a
                href="#how-it-works"
                class="inline-flex h-14 items-center gap-2 rounded-full border border-border/60 bg-white px-8 text-base font-semibold text-foreground transition-colors hover:bg-muted/60"
              >
                {{ $ts('hero.secondaryCta') }}
                <Icon name="tabler:chevron-down" size="18" class="opacity-60" />
              </a>
            </div>

            <!-- Trust strip -->
            <div class="mt-8 flex flex-wrap items-center justify-center gap-3">
              <!-- Trustpilot badge -->
              <span class="inline-flex items-center gap-2 rounded-full border border-border/60 bg-white px-4 py-2 text-sm font-semibold text-foreground shadow-sm">
                <Icon name="tabler:star-filled" size="14" class="text-emerald-500" />
                {{ $ts('hero.trustpilot') }}
              </span>
              <!-- Capterra badge -->
              <span class="inline-flex items-center gap-2 rounded-full border border-border/60 bg-white px-4 py-2 text-sm font-semibold text-foreground shadow-sm">
                <Icon name="tabler:award" size="14" class="text-orange-500" />
                {{ $ts('hero.capterra') }}
              </span>
              <span class="text-sm text-muted-foreground">{{ $ts('hero.trustNote') }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══ HOW IT WORKS ═══ -->
      <section id="how-it-works" class="bg-white py-20 md:py-28">
        <div class="mx-auto max-w-6xl px-4 md:px-6">
          <div class="mb-14 text-center">
            <p class="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {{ $ts('howItWorks.label') }}
            </p>
            <h2 class="text-4xl font-bold leading-[1.05] tracking-tight text-foreground md:text-5xl">
              {{ $ts('howItWorks.title') }}
            </h2>
            <p class="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
              {{ $ts('howItWorks.subtitle') }}
            </p>
          </div>

          <!-- Steps -->
          <div class="grid gap-5 md:grid-cols-3">
            <div
              v-for="(step, i) in steps"
              :key="step.key"
              class="group flex flex-col rounded-3xl border border-border/50 bg-white p-7 transition-transform hover:scale-[1.005]"
            >
              <div class="mb-5 flex items-center justify-between">
                <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-foreground text-sm font-bold text-background">
                  {{ i + 1 }}
                </span>
                <Icon :name="step.icon" size="32" class="text-muted-foreground/50" />
              </div>
              <h3 class="text-lg font-bold text-foreground">{{ $ts(`howItWorks.${step.key}.title`) }}</h3>
              <p class="mt-2 text-sm leading-relaxed text-muted-foreground">{{ $ts(`howItWorks.${step.key}.description`) }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══ FEATURES ═══ -->
      <section class="bg-muted/20 py-20 md:py-28">
        <div class="mx-auto max-w-6xl px-4 md:px-6">
          <div class="mb-14 text-center">
            <h2 class="text-4xl font-bold leading-[1.05] tracking-tight text-foreground md:text-5xl">
              {{ $ts('features.title') }}
            </h2>
            <p class="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
              {{ $ts('features.subtitle') }}
            </p>
          </div>

          <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div
              v-for="feature in features"
              :key="feature.key"
              class="group flex flex-col rounded-3xl border border-border/50 bg-white p-6 transition-transform hover:scale-[1.005]"
            >
              <div class="mb-4 flex items-center gap-3">
                <Icon :name="feature.icon" size="28" :class="['shrink-0', feature.color]" />
                <h3 class="text-base font-bold text-foreground">{{ $ts(`features.${feature.key}.title`) }}</h3>
              </div>
              <p class="text-sm leading-relaxed text-muted-foreground">{{ $ts(`features.${feature.key}.description`) }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══ SERVICE TYPES ═══ -->
      <section class="bg-white py-20 md:py-24">
        <div class="mx-auto max-w-6xl px-4 md:px-6">
          <div class="mb-12 text-center">
            <h2 class="text-4xl font-bold leading-[1.05] tracking-tight text-foreground md:text-5xl">
              {{ $ts('services.title') }}
            </h2>
            <p class="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
              {{ $ts('services.subtitle') }}
            </p>
          </div>

          <div class="flex flex-wrap justify-center gap-3">
            <span
              v-for="bt in businessTypes"
              :key="bt.key"
              class="inline-flex items-center gap-2 rounded-full border border-border/60 bg-white px-5 py-2.5 text-sm font-semibold text-foreground shadow-sm"
            >
              <Icon :name="bt.icon" size="16" class="text-muted-foreground" />
              {{ $ts(`businessTypes.${bt.key}`) }}
            </span>
          </div>
        </div>
      </section>

      <!-- ═══ PRICING ═══ -->
      <section class="bg-foreground py-20 md:py-28">
        <div class="mx-auto max-w-6xl px-4 md:px-6">
          <div class="mb-14 text-center">
            <h2 class="text-4xl font-bold leading-[1.05] tracking-tight text-background md:text-5xl">
              {{ $ts('pricing.title') }}
            </h2>
            <p class="mx-auto mt-4 max-w-xl text-lg text-background/60">
              {{ $ts('pricing.subtitle') }}
            </p>
          </div>

          <div class="grid gap-5 md:grid-cols-3">
            <div
              v-for="pillar in pricingPillars"
              :key="pillar.key"
              class="flex flex-col rounded-3xl border border-white/10 bg-white/10 p-7"
            >
              <div class="mb-4 flex items-center gap-3">
                <Icon :name="pillar.icon" size="28" class="shrink-0 text-white/70" />
                <h3 class="text-base font-bold text-background">{{ $ts(`pricing.${pillar.key}.title`) }}</h3>
              </div>
              <p class="text-sm leading-relaxed text-background/60">{{ $ts(`pricing.${pillar.key}.description`) }}</p>
            </div>
          </div>

          <div class="mt-10 text-center">
            <a
              :href="consoleUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex h-12 items-center gap-2 rounded-full border border-white/20 bg-white/10 px-7 text-sm font-semibold text-background transition-colors hover:bg-white/20"
            >
              {{ $ts('pricing.cta') }}
              <Icon name="tabler:arrow-right" size="16" />
            </a>
          </div>
        </div>
      </section>

      <!-- ═══ FINAL CTA ═══ -->
      <section class="relative overflow-hidden bg-white py-20 md:py-28">
        <div class="paw-pattern pointer-events-none absolute inset-0 opacity-[0.35]" aria-hidden="true" />
        <div class="relative mx-auto max-w-6xl px-4 text-center md:px-6">
          <h2 class="text-4xl font-bold leading-[1.05] tracking-tight text-foreground md:text-5xl lg:text-6xl">
            {{ $ts('finalCta.title') }}
          </h2>
          <p class="mx-auto mt-4 max-w-lg text-lg text-muted-foreground">
            {{ $ts('finalCta.subtitle') }}
          </p>
          <a
            :href="consoleUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-10 inline-flex h-14 items-center gap-2 rounded-full bg-foreground px-10 text-base font-semibold text-background transition-opacity hover:opacity-80"
          >
            {{ $ts('finalCta.primaryCta') }}
            <Icon name="tabler:arrow-right" size="18" />
          </a>
          <p class="mt-5 text-sm text-muted-foreground">{{ $ts('finalCta.note') }}</p>
        </div>
      </section>

    </main>
    <Footer />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Header from '#components/base/Header.vue';
import Footer from '#components/base/Footer.vue';
import { useI18n, useSeoMeta, useSchemaOrg, defineWebPage, useHead, useRuntimeConfig, useRoute } from '#imports';
import { useBreadcrumbSchema } from '#composables/useBreadcrumbSchema';

definePageMeta({
  layout: false,
});

const { $ts } = useI18n();

const route = useRoute();
const { public: { siteUrl, consoleUrl } } = useRuntimeConfig();

const locale = computed(() => route.params.locale as string | undefined);
const canonicalUrl = computed(() => {
  const prefix = locale.value && locale.value !== 'en' ? `/${locale.value}` : '';
  return `${siteUrl || 'https://bubba.pet'}${prefix}/partners`;
});

useSeoMeta({
  title: () => $ts('seo.title'),
  description: () => $ts('seo.description'),
});

useHead({
  link: [{ rel: 'canonical', href: () => canonicalUrl.value }],
});

useSchemaOrg([
  defineWebPage({ '@type': 'CollectionPage' }),
]);

useBreadcrumbSchema([
  { name: 'Home', item: '/' },
  { name: 'Partners' },
]);

const steps = [
  { key: 'step1', icon: 'tabler:user-plus' },
  { key: 'step2', icon: 'tabler:search' },
  { key: 'step3', icon: 'tabler:layout-dashboard' },
];

// Tailwind JIT safe — color classes must be static string literals.
const features = [
  { key: 'calendar',  icon: 'tabler:calendar',      color: 'text-vet' },
  { key: 'reminders', icon: 'tabler:bell-ringing',   color: 'text-groom' },
  { key: 'profile',   icon: 'tabler:map-pin',        color: 'text-emerald-500' },
  { key: 'analytics', icon: 'tabler:chart-bar',      color: 'text-blue-500' },
];

const businessTypes = [
  { key: 'vet',              icon: 'tabler:stethoscope' },
  { key: 'groomer',          icon: 'tabler:scissors' },
  { key: 'petBoarding',      icon: 'tabler:building' },
  { key: 'petTraining',      icon: 'tabler:school' },
  { key: 'breeder',          icon: 'tabler:heart' },
  { key: 'nonprofit',        icon: 'tabler:heart-handshake' },
  { key: 'marketplaceOnly',  icon: 'tabler:shopping-cart' },
];

const pricingPillars = [
  { key: 'listing',      icon: 'tabler:circle-check' },
  { key: 'commission',   icon: 'tabler:coin' },
  { key: 'subscription', icon: 'tabler:rocket' },
];
</script>
